import type { SpreadLayoutId } from '@kitup/book-schema';
import { request } from './client.js';

export type VideoStatus = 'draft' | 'queued' | 'generating' | 'ready' | 'failed';

export interface SignedVideoAssetDto {
  readonly assetId: string;
  readonly url: string;
  readonly urlExpiresAt: string;
}

export interface VideoVariantDto extends SignedVideoAssetDto {
  readonly id: string;
  readonly generationId: string | null;
  readonly seed: string | null;
  readonly modelId: string;
  readonly promptSnapshot: string;
  readonly createdAt: string;
}

export interface VideoSceneDto {
  readonly id: string;
  readonly bookId: string;
  readonly index: number;
  readonly animationPromptEn: string;
  readonly layoutId: SpreadLayoutId | null;
  readonly bandImageRatio: number | null;
  readonly imageAssetId: string | null;
  readonly videoAssetId: string | null;
  readonly status: VideoStatus;
  readonly lastError: string | null;
  readonly image: SignedVideoAssetDto | null;
  readonly video: SignedVideoAssetDto | null;
  readonly variants: readonly VideoVariantDto[];
}

export interface VideosDto {
  readonly enabled: boolean;
  readonly durationSec: number;
  readonly model: string;
  readonly items: readonly VideoSceneDto[];
  readonly replacements: {
    readonly used: number;
    readonly included: number;
    readonly extra: number;
    readonly unlimited: boolean;
    readonly remaining: number;
  };
  readonly progress: {
    readonly total: number;
    readonly queued: number;
    readonly generating: number;
    readonly ready: number;
    readonly failed: number;
    readonly waitingForImage: number;
    readonly estimatedSeconds: number;
  };
  readonly cost: {
    readonly spentUnits: string;
    readonly estimatedBatchUnits: string;
    readonly batchSceneCount: number;
  };
}

export const videosApi = {
  list: (bookId: string) => request<VideosDto>(`/books/${bookId}/videos`),
  generateAll: (bookId: string) =>
    request<{
      items: readonly {
        sceneId: string;
        generationId: string | null;
        status: 'queued' | 'ready' | 'failed';
        error: string | null;
      }[];
    }>(`/books/${bookId}/videos/generate`, {
      method: 'POST',
      body: { confirmCost: true },
    }),
  regenerate: (sceneId: string) =>
    request<{ generationId: string; status: 'queued' }>(`/scenes/${sceneId}/video/regenerate`, {
      method: 'POST',
    }),
  selectVariant: (sceneId: string, variantId: string) =>
    request<VideoSceneDto>(`/scenes/${sceneId}/video/select-variant`, {
      method: 'POST',
      body: { variantId },
    }),
};
